// ============================================================================
// Insurance tab — PURE SURFACE (TCSD-0097: shell owns the client and the state,
// the surface takes data and callbacks and imports nothing that talks to the
// database, so its copy can be rendered and asserted in a node test).
//
// ⛔ 'No COI on file' carries the SAME visual weight as 'Expired'. It is the
// larger population and it is invisible to every alert path we have, because a
// detector that compares expiry dates cannot fire on a provider that has none.
// ============================================================================
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { fmtBareDate } from '../../shared/format';

export type InsuranceScope = 'no_valid_coi' | 'expiring_30d' | 'all_working';

export const INSURANCE_SCOPE_LABEL: Record<InsuranceScope, string> = {
  no_valid_coi: 'No valid COI',
  expiring_30d: 'Expiring within 30 days',
  all_working: 'All working subs',
};

export const INSURANCE_SCOPE_ORDER: InsuranceScope[] = [
  'no_valid_coi',
  'expiring_30d',
  'all_working',
];

export interface InsuranceRow {
  provider_id: string;
  display_name: string;
  person: string | null;
  market_region: string | null;
  coi_state: string;
  coi_severity: number;
  coi_expiration_date: string | null;
  coi_doc_count: number;
  w9_state: string | null;
  active_assignments: number;
}

export function scopeFilter(rows: InsuranceRow[], scope: InsuranceScope): InsuranceRow[] {
  switch (scope) {
    case 'no_valid_coi':
      // severity 0 = No COI on file, 1 = Expired. Level with each other.
      return rows.filter((r) => r.coi_severity <= 1);
    case 'expiring_30d':
      return rows.filter((r) => r.coi_severity === 2 || r.coi_severity === 3);
    case 'all_working':
      return rows;
  }
}

/** Name the way an AM says it: business, then the person in parentheses. */
export function subLabel(r: InsuranceRow): string {
  return r.person ? `${r.display_name} (${r.person})` : r.display_name;
}

// A local YYYY-MM-DD renderer. ⛔ Never `new Date(bareDate)` — that parses as
// UTC midnight and renders the day before west of Greenwich (TCSD-0070).
// The parser now lives once, in shared/format; this keeps the em-dash for an
// empty date, which is what this table wants in a cell.
export function fmtLocalDate(d: string | null): string {
  return fmtBareDate(d) || '—';
}

function stateClass(severity: number): string {
  if (severity <= 1) return 'bg-st-danger-bg-strong text-st-danger-fg-strong';
  if (severity === 2) return 'bg-st-danger-bg text-st-danger-fg';
  if (severity === 3) return 'bg-st-warn-bg text-st-warn-fg-strong';
  return 'bg-st-ok-bg text-st-ok-fg';
}

export function UninsuredSubsView({
  rows,
  scope,
  onScope,
  counts,
  totalAssignments,
  renderName,
}: {
  rows: InsuranceRow[];
  scope: InsuranceScope;
  onScope: (s: InsuranceScope) => void;
  counts: Record<InsuranceScope, number>;
  totalAssignments: number;
  /** The shell supplies the link; the surface stays router-free. */
  renderName?: (r: InsuranceRow) => React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {INSURANCE_SCOPE_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={scope === s}
            onClick={() => onScope(s)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              scope === s
                ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-surface'
            }`}
          >
            {INSURANCE_SCOPE_LABEL[s]} ({counts[s]})
          </button>
        ))}
      </div>

      {scope === 'no_valid_coi' && rows.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-st-danger-border bg-st-danger-bg p-3 text-sm text-st-danger-fg-strong">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>
              {rows.length} subcontractor{rows.length === 1 ? '' : 's'} with live work at our
              facilities {rows.length === 1 ? 'has' : 'have'} no valid certificate of insurance
            </strong>
            , covering {totalAssignments} facility assignment
            {totalAssignments === 1 ? '' : 's'}. Upload a certificate on the sub's card to clear a
            row.
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-tcs-border bg-tcs-raised p-4 text-sm text-tcs-text-secondary">
          <ShieldCheck className="h-4 w-4 text-st-ok-fg" />
          Nothing in this group.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
          <table className="min-w-full divide-y divide-tcs-border text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2">Sub</th>
                <th className="px-3 py-2">COI</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2 text-right">Facilities</th>
                <th className="px-3 py-2">W-9</th>
                <th className="px-3 py-2">Market</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {rows.map((r) => (
                <tr key={r.provider_id} className="hover:bg-tcs-surface">
                  <td className="px-3 py-2">
                    <div className="font-medium text-tcs-text">
                      {renderName ? renderName(r) : subLabel(r)}
                    </div>
                    <div className="font-mono text-[11px] text-tcs-text-muted">{r.provider_id}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${stateClass(r.coi_severity)}`}
                    >
                      {r.coi_state}
                    </span>
                    {/* A certificate IS on file — it just carries no expiry
                        date, so nothing can judge it current. That is a data
                        gap to close, not a sub to chase for paperwork. */}
                    {r.coi_state === 'No COI on file' && r.coi_doc_count > 0 && (
                      <div className="mt-0.5 text-[11px] text-st-warn-fg-strong">
                        {r.coi_doc_count} certificate{r.coi_doc_count === 1 ? '' : 's'} on file with
                        no expiry date — add the date
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-tcs-text-secondary">
                    {fmtLocalDate(r.coi_expiration_date)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-tcs-text">
                    {r.active_assignments}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-tcs-text-secondary">
                    {r.w9_state ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-tcs-text-secondary">{r.market_region ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
